import {
  getRecordingS3IntakeSettings,
  IntegrationProvider,
} from "@repo/app-types/entities/integration";
import { parseRecordingIntakeBucketName } from "@repo/app-types/entities/recordingIntake";
import type { FC } from "react";
import { CopyableValue } from "./copyableValue.js";
import {
  integrationConfigs,
  RECORDING_S3_INTAKE_SLUG,
} from "./integrationConfigs.js";
import { RecordingS3IntakeAgentEmailMapping } from "./recordingS3IntakeAgentEmailMapping.js";
import { useRecordingS3IntakeDetailOutletContext } from "./recordingS3IntakeIntegrationDetailSection.js";
import { RecordingS3IntakeUploadSecret } from "./recordingS3IntakeUploadCredentials.js";

const config = integrationConfigs.find(
  (entry) => entry.slug === RECORDING_S3_INTAKE_SLUG,
);

interface FieldProps {
  title: string;
  description: string;
  value: string;
  label: string;
}

const Field: FC<FieldProps> = (props) => {
  const { title, description, value, label } = props;
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
      <CopyableValue value={value} label={label} />
    </div>
  );
};

/**
 * The "Recording S3 intake" Settings tab: read-only delivery details — the S3
 * bucket and region recordings are delivered to, the uploader credentials the
 * call center authenticates with (the secret revealed on request), and an
 * example of the expected file-name layout — plus, for RingCX customers, the
 * agent email mapping. Nothing here is editable: Alpharun provisions the bucket
 * and credentials and creates this integration from HQ, so there is no
 * connect/disconnect either.
 */
export const RecordingS3IntakeSettingsTab: FC = () => {
  const { integration } = useRecordingS3IntakeDetailOutletContext();
  const settings = getRecordingS3IntakeSettings(integration);

  const description = (
    <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
      {config?.description}
    </p>
  );

  if (!settings) {
    return <div className="space-y-6">{description}</div>;
  }

  const region = parseRecordingIntakeBucketName(settings.bucket)?.region;

  return (
    <div className="space-y-6">
      {description}

      {!settings.enabled ? (
        <div className="max-w-3xl rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Intake is currently paused by Alpharun.
        </div>
      ) : null}

      <div className="max-w-3xl space-y-5">
        <Field
          title="Bucket"
          description="Deliver call recordings to this Alpharun-owned S3 bucket."
          value={settings.bucket}
          label="bucket name"
        />

        {region ? (
          <Field
            title="Region"
            description="The AWS region to configure alongside the bucket."
            value={region}
            label="region"
          />
        ) : null}

        {settings.aws_access_key_id ? (
          <>
            <Field
              title="Access key ID"
              description="The AWS access key your call center authenticates uploads with."
              value={settings.aws_access_key_id}
              label="access key ID"
            />
            <RecordingS3IntakeUploadSecret />
          </>
        ) : null}

        <Field
          title="Example file name"
          description="An example of the file-name layout we expect. Actual values (the agent, number, and timestamp) vary per recording."
          value={settings.example_object_key}
          label="example file name"
        />

        {integration.provider ===
        IntegrationProvider.RINGCENTRAL_RECORDING_S3_INTAKE ? (
          <RecordingS3IntakeAgentEmailMapping />
        ) : null}
      </div>
    </div>
  );
};
